import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { triggerAiExtraction, type AiExtractionResult } from "../services/fafService";
import { cancelSanction } from "../services/disciplinaryService";
import type { DisciplinaryCase } from "../types";
import type { FafAuditMatchResult } from "../types/faf-audit.types";
import {
    buildReportCheckView,
    mapReportCheckRow,
    type ReportCheckDismissal,
    type ReportCheckMeta,
    type ReportCheckRow,
    type ReportCheckRpcRow,
    type ReportCheckView,
} from "../lib/pv/reportCheck";
import { useFafSanctionAudit } from "./useFafSanctionAudit";
import { useLeagueSeasonStore } from "../state/useLeagueSeasonStore";
import { SANCTION_DERIVED_QUERY_KEYS } from "./sanctionQueryKeys";
import { invalidatePVSessionQueries, invalidateSanctionPvStateQueries } from "./pvQueryInvalidation";

/**
 * Contrôle rapport FAF of one PV: pv_report_check rows (incidents, excessive
 * bookings, report status) + dismissals of the PV's matches + the cards of the
 * FAF sanction audit, folded by buildReportCheckView.
 */

const EMPTY_AUDIT: FafAuditMatchResult[] = [];

/** PostgREST « function not found » (PGRST202), or Postgres undefined_function (42883). */
export function isMissingFunctionError(error: { code?: string } | null | undefined): boolean {
    return error?.code === "PGRST202" || error?.code === "42883";
}

interface ReportCheckRows {
    /** pv_report_check does not exist on this database (migration not applied yet). */
    unavailable: boolean;
    rows: ReportCheckRow[];
}

export interface UsePVReportCheckViewArgs {
    sessionId: string | null | undefined;
    leagueId: string | null | undefined;
    season: string | null | undefined;
    cases: DisciplinaryCase[];
    matchMeta: Record<string, ReportCheckMeta | undefined>;
}

export interface ReportCheckRefreshResult {
    /** Report rows and dismissals reloaded. */
    ok: boolean;
    /** FAF cards reloaded — the panel still works without them. */
    cardsOk: boolean;
}

export function usePVReportCheckView({ sessionId, leagueId, season, cases, matchMeta }: UsePVReportCheckViewArgs) {
    const matchIds = useMemo(
        () =>
            Array.from(
                new Set(
                    cases
                        .filter((c) => (c.caseType ?? "match_sanction") === "match_sanction")
                        .map((c) => c.matchId)
                        .filter((id): id is string => !!id),
                ),
            ).sort(),
        [cases],
    );

    const rowsQuery = useQuery({
        queryKey: ["pvReportCheck", sessionId],
        enabled: !!sessionId,
        queryFn: async (): Promise<ReportCheckRows> => {
            const { data, error } = await supabase.rpc("pv_report_check", { p_session_id: sessionId });
            // The frontend ships on merge, before the migration: no RPC yet = no feature, not an error.
            if (error && isMissingFunctionError(error)) return { unavailable: true, rows: [] };
            if (error) throw error;
            return { unavailable: false, rows: ((data ?? []) as ReportCheckRpcRow[]).map(mapReportCheckRow) };
        },
    });
    const unavailable = rowsQuery.data?.unavailable === true;

    const dismissalsQuery = useQuery({
        queryKey: ["pvReportCheck", sessionId, "dismissals", matchIds],
        enabled: !!sessionId && matchIds.length > 0 && !unavailable,
        // Attaching a case changes matchIds: keep the previous dismissals meanwhile, no flash back to loading.
        placeholderData: keepPreviousData,
        queryFn: async (): Promise<ReportCheckDismissal[]> => {
            const { data, error } = await supabase
                .from("pv_report_check_dismissals")
                .select("match_id, fingerprint, note, dismissed_at")
                .in("match_id", matchIds);
            if (error) throw error;
            return ((data ?? []) as any[]).map((d) => ({
                matchId: d.match_id,
                fingerprint: d.fingerprint,
                note: d.note ?? null,
                dismissedAt: d.dismissed_at,
            }));
        },
    });

    const audit = useFafSanctionAudit(leagueId ?? null, season ?? null);
    const auditMatches = audit.data?.matches ?? EMPTY_AUDIT;
    const cardAudit = useMemo(() => {
        const wanted = new Set(matchIds);
        return auditMatches.filter((m) => wanted.has(m.matchId));
    }, [auditMatches, matchIds]);

    const dismissals = dismissalsQuery.data;
    const view: ReportCheckView | null = useMemo(() => {
        if (!rowsQuery.data || rowsQuery.data.unavailable) return null;
        if (matchIds.length > 0 && !dismissals) return null;
        return buildReportCheckView({ cases, matchMeta, rows: rowsQuery.data.rows, cardAudit, dismissals: dismissals ?? [] });
    }, [rowsQuery.data, dismissals, cases, matchMeta, cardAudit, matchIds.length]);

    return {
        view,
        /** The RPC is not deployed yet: the page hides the panel and the kanban badges. */
        unavailable,
        isLoading: !unavailable && (rowsQuery.isLoading || dismissalsQuery.isLoading),
        // The dismissals table ships with the RPC: its own error is moot while unavailable.
        isError: !unavailable && (rowsQuery.isError || dismissalsQuery.isError),
        error: unavailable ? null : ((rowsQuery.error ?? dismissalsQuery.error) as Error | null),
        cardsError: audit.isError,
        /** The FAF audit (cards) is still loading — the view is built from the rows meanwhile. */
        cardsLoading: audit.isLoading,
        /** A refetch of data already shown (the first load is `isLoading`). */
        isRefreshing:
            !unavailable &&
            ((rowsQuery.isFetching && !rowsQuery.isLoading) ||
                (dismissalsQuery.isFetching && !dismissalsQuery.isLoading) ||
                (!!audit.isFetching && !audit.isLoading)),
        /** Resolves once the rows, dismissals and cards are all back. */
        refetch: async (): Promise<ReportCheckRefreshResult> => {
            // « Réessayer » after cardsError retries the cards too.
            const [rows, dismissed, cards] = await Promise.all([
                rowsQuery.refetch(),
                dismissalsQuery.refetch(),
                audit.refetch(),
            ]);
            return { ok: !rows.isError && !dismissed.isError, cardsOk: !cards?.isError };
        },
    };
}

export interface ReportFindingKey {
    matchId: string;
    fingerprint: string;
}

export function useDismissReportFinding() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ matchId, fingerprint, note }: ReportFindingKey & { note: string | null }) => {
            const { error } = await supabase
                .from("pv_report_check_dismissals")
                .insert({ match_id: matchId, fingerprint, note });
            // Already dismissed (another tab, double click): same end state.
            if (error && error.code !== "23505") throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["pvReportCheck"] });
        },
    });
}

export function useRestoreReportFinding() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ matchId, fingerprint }: ReportFindingKey) => {
            const { error } = await supabase
                .from("pv_report_check_dismissals")
                .delete()
                .eq("match_id", matchId)
                .eq("fingerprint", fingerprint);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["pvReportCheck"] });
        },
    });
}

/**
 * « Couvert par cette sanction » : the sanction's motif now covers the
 * incident's family, in this PV and every later one (set_motif_report_family).
 */
export function useCoverReportFindingWithMotif() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ reasonId, family }: { reasonId: string; family: string }) => {
            const { error } = await supabase.rpc("set_motif_report_family", {
                p_reason_id: reasonId,
                p_family: family,
                p_covers: true,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["pvReportCheck"] });
            qc.invalidateQueries({ queryKey: ["disciplinaryReasons"] });
        },
    });
}

/**
 * « Annuler » on one sanction of a duplicate group: the same cancel as the
 * match entry (is_cancelled, FAF link released), then every view of it.
 */
export function useCancelReportSanction(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ sanctionId }: { sanctionId: string }) => {
            const { data } = await supabase.auth.getSession();
            await cancelSanction(supabase, sanctionId, data.session?.user.id ?? null);
        },
        onSuccess: () => {
            invalidatePVSessionQueries(qc, sessionId);
            for (const key of ["sanctions", "paginatedSanctions", "sanctionsCount", "disciplinaryAudit", "fafImportedEventIds", ...SANCTION_DERIVED_QUERY_KEYS]) {
                qc.invalidateQueries({ queryKey: [key] });
            }
        },
        // Refused (PV published in another tab): the board must show it published.
        onError: () => invalidateSanctionPvStateQueries(qc, sessionId),
    });
}

/** « Analyser » — reuses a fresh cache, never forces a paid re-extraction. */
export function useAnalyseReport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ fafMatchId }: { fafMatchId: string }): Promise<AiExtractionResult> => {
            const result = await triggerAiExtraction(supabase, fafMatchId, false);
            // A blank report is an answer, not a failure: the caller shows « Rapport vide ».
            if (result.error && !result.blank) throw new Error(result.error);
            return result;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["pvReportCheck"] });
            qc.invalidateQueries({ queryKey: ["fafSanctionAudit"] });
        },
    });
}

export interface PVReportCheckPending {
    sessionId: string;
    meetingDate: string | null;
    publicationNumber: string | null;
    committeeName: string | null;
    alerts: number;
    attach: number;
    /** Duplicate sanctions still open; 0 before 20260930120000. */
    duplicates: number;
}

/**
 * Draft PVs of the selected league/season whose report check still has
 * « à sanctionner », « hors PV » or « doublon » findings (FAF cards excluded): the
 * notification bell's Discipline items. Under the ["pvReportCheck"] prefix, so
 * every report-check and PV mutation clears a treated PV from the bell.
 */
export function usePVReportCheckPending(enabled: boolean) {
    const leagueId = useLeagueSeasonStore((s) => s.currentLeagueId);
    const season = useLeagueSeasonStore((s) => s.currentSeason);
    return useQuery({
        queryKey: ["pvReportCheck", "pending", leagueId, season],
        enabled: enabled && !!leagueId && !!season,
        staleTime: 2 * 60 * 1000,
        queryFn: async (): Promise<PVReportCheckPending[]> => {
            const { data, error } = await supabase.rpc("pv_report_check_pending", {
                p_league_id: leagueId,
                p_season: season,
            });
            // Frontend ships before the migration: no RPC yet = nothing to announce.
            if (error && isMissingFunctionError(error)) return [];
            if (error) throw error;
            return ((data ?? []) as any[]).map((r) => ({
                sessionId: r.session_id as string,
                meetingDate: (r.meeting_date ?? null) as string | null,
                publicationNumber: (r.publication_number ?? null) as string | null,
                committeeName: (r.committee_name ?? null) as string | null,
                alerts: Number(r.alerts) || 0,
                attach: Number(r.attach) || 0,
                duplicates: Number(r.duplicates) || 0,
            }));
        },
    });
}
